import { useTranslation } from 'react-i18next';
import { CheckCircle2, Hourglass, Loader2, UserPlus } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import type { StageParticipation } from '../../types/trophy';

interface StageEntryActionProps {
  stage: StageParticipation;
  /**
   * Whether somebody is signed in. A visitor gets the same button: what they
   * are asked next — themselves, or a child — is what decides which kind of
   * account the sign-up opens (see EntryIdentityDialog).
   */
  signedIn: boolean;
  /** This stage's button is waiting on the question behind it being prepared. */
  busy?: boolean;
  /**
   * Somebody else this account can still enter here — a child whose managed
   * account it holds. Without it a parent who entered themselves would have a
   * tick and nothing else, and no way left to enter their children.
   */
  othersToEnter?: boolean;
  /**
   * Whose entry this button makes, when it is not the viewer's own: a child
   * whose managed account they hold. Carried through to the confirmation so
   * the dialog can name them.
   */
  athlete?: { user_id: string; full_name: string } | null;
  /** Asks for confirmation; the page owns the dialog and the request. */
  onRequestEnter: (
    stage: StageParticipation,
    athlete?: { user_id: string; full_name: string } | null
  ) => void;
}

/**
 * Where the viewer stands at one stage, and the one thing they can do about
 * it. In: a tick, with the category the desk put them in. Not in, and the
 * stage still taking entries: a button — the same button whether or not they
 * are signed in, because a visitor who taps it is asked who is climbing and
 * sent to the right sign-up. A closed stage they are not in says nothing —
 * its status badge already does.
 *
 * The one state that is neither a tick nor a button is a stage held up by its
 * organiser (`entry_block`): the desk's hours are still running, so the page
 * would otherwise read "entries open" with nothing beside it, and the entry
 * endpoint would refuse anybody who found a way to ask. Why it is held up — a
 * data processing agreement nobody has signed — is told only to whoever works
 * the desk (`dpa_required`); everybody else reads that entries are on hold.
 */
export default function StageEntryAction({
  stage,
  signedIn,
  busy = false,
  othersToEnter = false,
  athlete = null,
  onRequestEnter,
}: StageEntryActionProps) {
  const { t } = useTranslation();

  if (stage.entered) {
    return (
      <span
        className="flex flex-col items-end gap-0.5"
        data-testid="stage-entry-status"
        data-state="entered"
      >
        <Badge variant="outline" className="gap-1 border-green-600 text-green-600">
          <CheckCircle2 className="size-3" aria-hidden />
          {t('stageEntry.entered')}
        </Badge>
        {(stage.entry?.category_name || stage.entry?.paid === false) && (
          <span className="text-[11px] text-muted-foreground">
            {[stage.entry?.category_name, stage.entry?.paid === false ? t('stageEntry.unpaid') : null]
              .filter(Boolean)
              .join(' · ')}
          </span>
        )}
        {/* In themselves, with a child still to enter: the tick is the whole
            answer for them and none of it for the child. */}
        {othersToEnter && stage.open && (
          <Button
            size="sm"
            variant="outline"
            className="mt-1 gap-1.5"
            disabled={busy}
            onClick={() => onRequestEnter(stage, athlete)}
            data-testid="enter-another-athlete"
          >
            {busy ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <UserPlus className="size-4" aria-hidden />
            )}
            {t('stageEntry.enterAnother')}
          </Button>
        )}
      </span>
    );
  }

  // `can_enter` is the signed-in viewer's own answer; a visitor gets here on
  // `open` below. Both end in the same button and the same question.
  if (stage.can_enter || (stage.open && !signedIn)) {
    return (
      <Button
        size="sm"
        className="gap-1.5"
        disabled={busy}
        onClick={() => onRequestEnter(stage, athlete)}
        data-testid="stage-entry-status"
        data-state={signedIn ? 'open' : 'sign-up'}
      >
        {busy ? (
          <Loader2 className="size-4 animate-spin" aria-hidden />
        ) : (
          <UserPlus className="size-4" aria-hidden />
        )}
        {t('stageEntry.enter')}
      </Button>
    );
  }

  // Nobody may enter, whoever they are and whether or not they are signed in:
  // the organiser has to sign the agreement we would hold their entrants under
  // before we take one more. Signing in would change nothing, so a visitor is
  // told the same thing as everybody else rather than sent off to an account
  // that cannot enter either.
  if (stage.entry_block) {
    return (
      // A sentence rather than a badge: the row's own status badge already
      // says "entries on hold" (see competitionBadge), and what is missing is
      // why. Capped, because left to grow it takes a phone's whole row and
      // squeezes the stage's name down to an ellipsis.
      <span
        className="flex max-w-[11rem] items-start gap-1.5 text-[11px] leading-snug text-muted-foreground"
        data-testid="stage-entry-status"
        data-state="blocked"
      >
        <Hourglass className="mt-px size-3 shrink-0" aria-hidden />
        {t(stage.entry_block === 'dpa_required' ? 'stageEntry.blockedHint' : 'stageEntry.onHoldHint')}
      </span>
    );
  }

  return null;
}
